import { zodResolver } from '@hookform/resolvers/zod';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, { useEffect, useState } from 'react';
import { useForm, useController, useFormState, Controller, SubmitHandler } from 'react-hook-form';
import MaskedInput, { conformToMask } from 'react-text-mask';
import { z } from 'zod';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import CloseSvg from '@/components/common/CloseSvg';
import HidePasswordSvg from '@/components/common/HidePasswordSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import ViewPasswordSvg from '@/components/common/ViewPasswordSvg';
import {
    isValidContainNumber,
    isValidTooSmall,
    isValidSpecial,
    isValidLower,
    isValidUpper,
    isValidUpperLower,
} from '@/components/myaccount/utils/validation';
import { Button } from '@/components/ui/button';
import {
    addressAutoCompleteCheckoutFind,
    addressAutoCompleteCheckoutRetrieve,
} from '@/external/service/canadapostaddress.service';
import { TmAccountRequest } from '@/tscapi/model/request/account-request';
import { Cookie } from '@/tscapi/utils/cookie';

import { formatCreateAddress, convertToBoolean } from '../../myaccount/utils/address.utils';
import { useCreateAccount } from '../hooks/useCreateAccount';
import { AccountSchema } from '../schemas/account.schema';

declare let grecaptcha: any;
declare let reCaptchaEnabled: any;
const province = [
    { name: 'Alberta', id: 'AB' },
    { name: 'British Columbia', id: 'BC' },
    { name: 'Manitoba', id: 'MB' },
    { name: 'New Brunswick', id: 'NB' },
    { name: 'Newfoundland and Labrador', id: 'NL' },
    { name: 'Northwest Territories', id: 'NT' },
    { name: 'Nova Scotia', id: 'NS' },
    { name: 'Nunavut', id: 'NU' },
    { name: 'Ontario', id: 'ON' },
    { name: 'Prince Edward Island', id: 'PE' },
    { name: 'Quebec', id: 'QC' },
    { name: 'Saskatchewan', id: 'SK' },
    { name: 'Yukon Territory', id: 'YT' },
];
interface CanadaPostAddressData {
    Cursor?: number;
    Description: string;
    Highlight?: string;
    Id: string;
    Next?: string;
    Text: string;
}
interface CreateAccountProps {
    name: string;
}
type ValidationAccountSchema = z.infer<typeof AccountSchema>;
type Province = z.infer<typeof AccountSchema>['province'];
function upperCasePipe(conformedValue) {
    return conformedValue.toUpperCase();
}

export const CreateAccount = ({ name }: CreateAccountProps) => {
    const mutation = useCreateAccount();
    const isMutating = useIsMutating();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);
    const [formError, setFormError] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const toggleShowNewPassword = () => setShowNewPassword((prevState) => !prevState);
    const toggleShowRetypePassword = () => setShowRetypePassword((prevState) => !prevState);
    const {
        handleSubmit,
        register,
        control,
        setError,
        watch,
        trigger,
        setValue,
        clearErrors,
        getFieldState,
        formState: { errors, isDirty, isValid, dirtyFields, touchedFields },
    } = useForm<ValidationAccountSchema>({
        resolver: zodResolver(AccountSchema),
        mode: 'onBlur',
        criteriaMode: 'all',
        defaultValues: {
            firstname: '',
            lastname: '',
            dayphone: '',
            address: '',
            address2: '',
            province: 'a' as Province,
            postalcode: '',
            email: '',
            city: '',
            defaultShipping: false,
            subscribe: false,
            newPassword: '',
            retypeNewPassword: '',
        },
    });
    const { onChange: onProvinceChange, ...provinceField } = register('province');
    const [addressList, setAddressList] = useState<CanadaPostAddressData[]>([]);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const {
        field: { ...field },
        fieldState,
    } = useController({
        name: 'address',
        control,
    });
    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    useEffect(() => {
        const loadReCaptcha = () => {
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + reCaptchaSiteKey;
            document.body.appendChild(script);
        };
        loadReCaptcha();
    }, []);

    const passw = watch('retypeNewPassword');
    const onSubmit: SubmitHandler<ValidationAccountSchema> = (data) => {
        if (hasSubmitted) return;
        setHasSubmitted(true);

        grecaptcha.ready(function () {
            grecaptcha
                .execute(reCaptchaSiteKey, { action: 'createaccount' })
                .then((token) => {
                    const addressPayload = formatCreateAddress(data);
                    const extraAccountPayload = {
                        Email: data.email,
                        Subscribe: convertToBoolean(data.subscribe),
                        Password: data.newPassword,
                        RetypedPassword: data.retypeNewPassword,
                        ReCaptchaToken: token,
                    };
                    const formData = { ...addressPayload, ...extraAccountPayload };
                    //const formData1 = Object.assign(addressPayload, extraAccountPayload);
                    mutation.mutate(formData, {
                        onSuccess: () => {
                            if (formData.Subscribe) {
                                const subscribeEvent = new CustomEvent('subscribe', {
                                    detail: {
                                        Source: 'CreateAccount',
                                    },
                                });
                                document.dispatchEvent(subscribeEvent);
                            }

                            const checkoutPixelEvent = new CustomEvent('complete-registration', { detail: data });
                            document.dispatchEvent(checkoutPixelEvent);
                            setTimeout(() => {
                                const cookie = new Cookie();
                                cookie.deleteTargetUrl();
                                window.location.href = '/pages/myaccount';
                            }, 300);
                        },

                        onError: (error) => {
                            setFormError(error as string);
                            setHasSubmitted(false);
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth',
                            });
                        },
                    });
                })
                .catch(() => {
                    setHasSubmitted(false);
                });
        });

        //handleFormSubmit(formData);
    };
    const {
        isOpen,
        openMenu,
        getLabelProps,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        setInputValue,
        selectedItem,
    } = useCombobox({
        items: addressList,
        itemToString(item) {
            return item ? `${item.Text}` : '';
        },
        onSelectedItemChange: ({ inputValue, selectedItem }) => {
            // check canpost type and if find, get new set
            if (selectedItem.Id == 'optOut') {
                setValue('city', '');
                setValue('province', 'a');
                setValue('postalcode', '');
            } else if (selectedItem.Next == 'Retrieve') {
                //clear form fields for street,city,state,zip
                retrieveAddress(selectedItem.Id);
                return;
            } else if (selectedItem && selectedItem.Next == 'Find') {
                findAddress(inputValue, selectedItem.Id);
                openMenu();
                return;
            } else {
                field.onChange(inputValue);
            }
        },
        onInputValueChange({ inputValue }) {
            // onChange(inputValue);
            if (inputValue.length > 1) {
                findAddress(inputValue, undefined);
            }
            //setInputValue(inputValue);
            //setItems(books.filter(getBooksFilter(inputValue)));
        },
    });
    const retrieveAddress = async (id) => {
        try {
            const response = await addressAutoCompleteCheckoutRetrieve(id);
            let selectedAddress;
            if (response && response.status == 200) {
                selectedAddress = response.data.Items[0] as CanadaPostAddressData;
                field.onChange(selectedAddress.Line1);
                setValue('address2', selectedAddress.Line2);
                setValue('city', selectedAddress.City);
                setValue('province', selectedAddress.Province);
                setValue('postalcode', selectedAddress.PostalCode);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };
    const findAddress = async (value, lastId) => {
        let streetAddresses: CanadaPostAddressData[] = [];

        try {
            const response = await addressAutoCompleteCheckoutFind(value, lastId);
            if (response && response.status == 200) {
                if (response.data.Items && response.data.Items.length > 0 && response.data.Items[0].Error) {
                    streetAddresses = [];
                } else {
                    streetAddresses = response.data.Items;
                    streetAddresses.push({
                        Text: "I don't see my address. Let me type it in.",
                        Description: '',
                        Id: 'optOut',
                        Next: '',
                    });
                }
                setAddressList(streetAddresses);
            }
        } catch (error) {
            console.log('error:' + error);
        }
    };

    return (
        <div className="px-5 pb-32 pt-9 md:mx-auto md:max-w-[560px]">
            {name}
            <h1 className="m-0 pb-2 text-xl font-bold text-[#1a1a1a] md:pb-4 md:text-center md:text-4xl">
                Create a new account
            </h1>
            <p className="text-base font-light md:text-center">
                Already shopped with us by phone?{' '}
                <a href="/pages/createphoneaccount" className="underline hover:underline">
                    Transfer your phone account
                </a>
            </p>
            {formError && (
                <div className="notification__red x relative mt-4 flex items-start border-2 border-solid border-red-600 py-5 pl-2 pr-7 text-sm">
                    <img
                        src="/App_Assets/public/icon-exclamation-black.svg"
                        alt="exclamation-icon"
                        className="exclamation-img mr-2 max-w-[20px]"
                    />
                    <div>
                        <strong className="">{formError}</strong>
                    </div>
                </div>
            )}
            <div className="mb-7 mt-10">Required fields are marked with an asterisk (*)</div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 gap-0 pb-6 md:pb-10">
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            maxLength={16}
                            placeholder=" "
                            data-error={getFieldState('firstname').error ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="given-name"
                            {...register('firstname')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="firstname"
                        >
                            First name *
                        </label>
                        {errors.firstname && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.firstname.message}</div>
                        )}
                    </div>
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={16}
                            data-error={getFieldState('lastname').error ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="family-name"
                            {...register('lastname')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="lastname"
                        >
                            Last name *
                        </label>
                        {errors.lastname && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.lastname.message}</div>
                        )}
                    </div>
                </div>
                <div className="z-1 relative mb-6">
                    <div className="relative">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={30}
                            {...getInputProps({
                                ...field,
                                onChange: (event) => {
                                    field.onChange(event as React.ChangeEvent<HTMLInputElement>);
                                },
                            })}
                            data-error={getFieldState('address').error ? 'true' : 'false'}
                            // data-empty={addressWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="billing address-line1"
                            data-qm-encrypt="true"
                        />
                        <label
                            {...getLabelProps()}
                            className="pointer-events-none absolute top-2.5 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                            htmlFor="address"
                        >
                            Street and house number *
                        </label>
                        <ul
                            className={`absolute z-10 max-h-80 w-full overflow-y-auto border border-solid border-[#e3e3e3] bg-white p-0 px-1 py-1 shadow-md ${
                                !(isOpen && addressList.length) && 'hidden'
                            }`}
                            {...getMenuProps()}
                        >
                            {isOpen &&
                                addressList.map((item, index) => (
                                    <li
                                        className={classNames(
                                            highlightedIndex === index && 'bg-gray-200',
                                            selectedItem === item && 'font-bold',
                                            'z-10 flex flex-col px-3 py-2 shadow-sm',
                                        )}
                                        key={item.Id}
                                        {...getItemProps({ item, index })}
                                    >
                                        <span className="text-sm text-gray-700">
                                            {item.Text} {item.Description}
                                        </span>
                                    </li>
                                ))}
                        </ul>
                    </div>
                    {errors.address && <div className="mt-1 text-xs text-[#df0c0c]">{errors.address.message}</div>}
                </div>
                <div className="relative z-0 mb-6">
                    <input
                        type="text"
                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                        placeholder=" "
                        maxLength={30}
                        data-error={getFieldState('address2').error ? 'true' : 'false'}
                        // data-empty={addressWatch === '' ? 'true' : 'false'}
                        tabIndex={0}
                        {...register('address2')}
                        data-qm-encrypt="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                        htmlFor="address2"
                    >
                        Apartment, suite, etc
                    </label>
                    {errors.address2 && <div className="mt-1 text-xs text-[#df0c0c]">{errors.address2.message}</div>}
                </div>
                <div className="grid grid-cols-1 gap-0 md2:grid-cols-2 md2:gap-5">
                    <div className="relative z-0 mb-6">
                        <Controller
                            name="dayphone"
                            control={control}
                            // defaultValue={selectedCreditCard?.MaskedNumber || ''}
                            render={({ field, fieldState }) => (
                                <>
                                    <div className="">
                                        <MaskedInput
                                            mask={[
                                                '(',
                                                /[1-9]/,
                                                /\d/,
                                                /\d/,
                                                ')',
                                                ' ',
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                                '-',
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                                /\d/,
                                            ]}
                                            {...field}
                                            // data-empty={tscCardWatch === '' ? 'true' : 'false'}
                                            data-error={getFieldState('dayphone').error ? 'true' : 'false'}
                                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                            guide={false}
                                            placeholder=" "
                                            autoComplete="billing tel"
                                            onFocus={() => {
                                                // debugger;
                                                // if (!getFieldState('dayphone').isDirty && field.value !== '') {
                                                //     setValue('dayphone', '');
                                                // }
                                            }}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                if (errors.dayphone) {
                                                    clearErrors('dayphone');
                                                }
                                            }}
                                            tabIndex={0}
                                            type="tel"
                                            data-qm-encrypt="true"
                                        />
                                        <label
                                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                            htmlFor="dayphone"
                                        >
                                            Phone number *
                                        </label>

                                        {fieldState.error && (
                                            <div className="mt-1 text-xs text-[#df0c0c]">
                                                {fieldState.error.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        />
                    </div>
                    <div className="relative z-0 mb-6">
                        <input
                            type="text"
                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                            placeholder=" "
                            maxLength={30}
                            data-error={getFieldState('city').error ? 'true' : 'false'}
                            // data-empty={lastnameWatch === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="billing address-level2"
                            {...register('city')}
                            data-qm-encrypt="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="city"
                        >
                            City *
                        </label>
                        {errors.city && <div className="mt-1 text-xs text-[#df0c0c]">{errors.city.message}</div>}
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-0 pb-8 md2:grid-cols-2 md2:gap-5">
                    <div className="relative z-0 mb-6">
                        <select
                            className={`w-full appearance-none border-0 border-b bg-transparent bg-[url('/App_Assets/public/selectbox-chevron.svg')] bg-[calc(100%_-_5px)] bg-no-repeat px-0 pb-1.5 pr-7 pt-2.5 text-base font-medium focus-within:outline-none focus-visible:shadow-lg ${getFieldState('province').error ? 'border-[#df0c0c]' : 'border-black'}`}
                            {...provinceField}
                            autoComplete="billing address-level1"
                            // defaultValue={defaultProvince}
                            onChange={(e) => {
                                onProvinceChange(e);
                                // resetField('answer');
                            }}
                        >
                            <option key="a" value="a">
                                Province *
                            </option>
                            {province &&
                                province.map((province) => (
                                    <option key={province.id} value={province.id}>
                                        {province.name}
                                    </option>
                                ))}
                        </select>
                        {errors.province && (
                            <div className="mt-1 text-xs text-[#df0c0c]">{errors.province.message}</div>
                        )}
                    </div>
                    <div className="relative z-0 mb-6">
                        <Controller
                            name="postalcode"
                            control={control}
                            // defaultValue={selectedCreditCard?.MaskedNumber || ''}
                            render={({ field, fieldState }) => (
                                <>
                                    <div className="">
                                        <MaskedInput
                                            mask={[/[A-Z]/i, /\d/, /[A-Z]/i, ' ', /\d/, /[A-Z]/i, /\d/]}
                                            {...field}
                                            // data-empty={tscCardWatch === '' ? 'true' : 'false'}
                                            data-error={getFieldState('postalcode').error ? 'true' : 'false'}
                                            className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                                            guide={false}
                                            placeholder=" "
                                            pipe={upperCasePipe}
                                            autoComplete="billing postal-code"
                                            // onFocus={() => {
                                            //     if (!getFieldState('postalcode').isDirty && field.value !== '') {
                                            //         setValue('postalcode', '');
                                            //     }
                                            // }}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                if (errors.postalcode) {
                                                    clearErrors('postalcode');
                                                }
                                            }}
                                            tabIndex={0}
                                            type="text"
                                            data-qm-encrypt="true"
                                        />
                                        <label
                                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                            htmlFor="postalcode"
                                        >
                                            Postal code *
                                        </label>

                                        {fieldState.error && (
                                            <div className="mt-1 text-xs text-[#df0c0c]">
                                                {fieldState.error.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        />
                    </div>
                </div>
                <div className="relative z-0 mb-6">
                    <input
                        type="text"
                        className="peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg data-[error=true]:border-[#df0c0c]"
                        placeholder=" "
                        maxLength={50}
                        data-error={getFieldState('email').error ? 'true' : 'false'}
                        // data-empty={addressWatch === '' ? 'true' : 'false'}
                        tabIndex={0}
                        {...register('email')}
                        data-qm-encrypt="true"
                    />
                    <label
                        className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-[30px] scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:-translate-y-2.5 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-[30px] peer-focus:scale-75"
                        htmlFor="email"
                    >
                        Email address *
                    </label>
                    {errors.email && <div className="mt-1 text-xs text-[#df0c0c]">{errors.email.message}</div>}
                </div>
                <div className="tscform__checkbox--wrap mb-5 mt-7 flex justify-items-start pb-6">
                    <Controller
                        name="subscribe"
                        control={control}
                        render={({ field, fieldState }) => (
                            <>
                                <Checkbox.Root
                                    checked={field.value}
                                    //defaultChecked={selectedCreditCard?.IsDefault}
                                    onCheckedChange={(checked) => {
                                        field.onChange(checked);
                                    }}
                                    className={`tscform__checkbox peer flex h-5 w-5 flex-shrink-0 cursor-pointer border border-solid border-black disabled:cursor-default data-[state=checked]:bg-black data-[state=checked]:disabled:border-[#a3a3a3] data-[state=checked]:disabled:bg-[#a3a3a3]`}
                                    id="c2"
                                ></Checkbox.Root>
                            </>
                        )}
                    />
                    <label
                        className="mb-0 cursor-pointer select-none pl-2.5 text-[13px] font-light peer-disabled:cursor-default"
                        htmlFor="c2"
                    >
                        Sign me up for Special Offers & Today&apos;s Showstopper Emails. I understand that I may
                        withdraw my consent at any time.{' '}
                        <a
                            target="_blank"
                            href="/go/aboutus/servicespage#privacypolicy"
                            className="underline hover:underline"
                        >
                            Our Privacy Policy
                        </a>
                    </label>
                </div>
                <div className="mb-4 pb-4">
                    {/* --------- new password --------- */}
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <input
                            className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                            type={showNewPassword ? 'text' : 'password'}
                            maxLength={16}
                            placeholder=" "
                            data-error={getFieldState('newPassword').error ? 'true' : 'false'}
                            // data-empty={inputNew === '' ? 'true' : 'false'}
                            tabIndex={0}
                            autoComplete="new-password"
                            {...register('newPassword', {
                                onChange: (e) => {
                                    trigger('newPassword');
                                    if (passw != '') {
                                        trigger('retypeNewPassword');
                                    }
                                },
                            })}
                            data-qm-block="true"
                        />
                        <label
                            className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                            htmlFor="newPassword"
                        >
                            Password *
                        </label>

                        <button
                            className="absolute bottom-0 right-0 z-[2]"
                            type="button"
                            onClick={toggleShowNewPassword}
                            tabIndex={-1}
                        >
                            {showNewPassword ? (
                                <ViewPasswordSvg width="20" height="19" />
                            ) : (
                                <HidePasswordSvg width="20" height="20" />
                            )}
                        </button>
                    </div>

                    {(getFieldState('newPassword').isTouched || getFieldState('newPassword').isDirty) && (
                        <div className="px-5 pb-7 pt-0 text-[13px]">
                            <div className="font-semibold">Your password must have:</div>
                            <div className="flex flex-col">
                                <div
                                    className={
                                        isValidTooSmall(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">at least 8 characters</span>
                                </div>
                                <div
                                    className={
                                        isValidUpper(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 upper case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidLower(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 lower case letter</span>
                                </div>
                                <div
                                    className={
                                        isValidContainNumber(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">include at least 1 number</span>
                                </div>
                                <div
                                    className={
                                        isValidSpecial(errors)
                                            ? 'valid group pt-2 text-green-700'
                                            : 'group pt-2 text-red-500'
                                    }
                                >
                                    <CheckmarkSmallSvg className="hidden group-[.valid]:!inline" />
                                    <CloseSvg className="inline group-[.valid]:hidden" width="14" height="12" />
                                    <span className="pl-2.5">
                                        include at least 1 special character
                                        <span className="max-md:hidden md:inline">
                                            {' '}
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                        <span className="max-md:block max-md:pl-6 md:hidden">
                                            (e.g. !, @, #, $, %, ^, &, *, or ~)
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --------- retype password --------- */}
                    <div className="relative z-0 mb-6 flex flex-col justify-center">
                        <div className="relative z-0 flex flex-col justify-center">
                            <input
                                className="profile__form-input peer mt-2.5 block w-full appearance-none rounded-none border-0 border-b border-black bg-transparent px-0 py-1 text-base font-light text-[#1a1a1a] focus-within:outline-none focus:outline-none focus:ring-0 focus-visible:shadow-lg focus-visible:ring-[#f8f8f8] data-[error=true]:border-[#df0c0c]"
                                type={showRetypePassword ? 'text' : 'password'}
                                // data-empty={inputRetype === '' ? 'true' : 'false'}
                                data-error={getFieldState('retypeNewPassword').error ? 'true' : 'false'}
                                tabIndex={0}
                                placeholder=" "
                                maxLength={16}
                                autoComplete="new-password"
                                {...register('retypeNewPassword')}
                                data-qm-block="true"
                            />
                            <label
                                className="absolute top-2.5 -z-10 mb-0 origin-[0] -translate-y-5 scale-75 transform text-base font-medium duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-5 peer-focus:scale-75"
                                htmlFor="retypeNewPassword"
                            >
                                Retype password *
                            </label>

                            <button
                                className="absolute bottom-0 right-0 z-[2]"
                                type="button"
                                onClick={toggleShowRetypePassword}
                                tabIndex={-1}
                            >
                                {showRetypePassword ? (
                                    <ViewPasswordSvg width="20" height="19" />
                                ) : (
                                    <HidePasswordSvg width="20" height="20" />
                                )}
                            </button>
                        </div>
                        {errors.retypeNewPassword && (
                            <div className="mt-1 font-semibold text-[#df0c0c]">{errors.retypeNewPassword.message}</div>
                        )}

                        {/* {mutation.isError ? <div className="profile__error">{mutation.error}</div> : null} */}
                    </div>
                </div>
                <div className="relative z-0 mb-6">
                    <p className="font-light">
                        By clicking &quot;Create account&quot; below, I acknowledge and agree that the information I am
                        providing will be subject to the TSC privacy policy, and I confirm my acceptance of the TSC
                        Terms of Use.
                    </p>
                </div>
                {/* --------- Submit --------- */}
                <div>
                    <div className="mb-7 mt-4 grid grid-cols-1">
                        <Button
                            type="submit"
                            aria-label={'Create account'}
                            disabled={!isValid || mutation.isLoading || hasSubmitted}
                            tabIndex={!isValid ? -1 : 0}
                        >
                            Create account
                        </Button>
                    </div>
                </div>
                <div className="text-left">
                    <a className="flex justify-start no-underline" href="/pages/signin">
                        <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            viewBox="0 0 45 45"
                        >
                            <g fill="none" fillRule="evenodd">
                                <path
                                    stroke="#000"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    className="chevron"
                                    d="M26 13 16 23l10 10"
                                />
                            </g>
                        </svg>
                        <span className="underline">Back to Sign-in</span>
                    </a>
                </div>
                {mutation.isLoading || isMutating ? (
                    <div className="blockPageWrap" style={{ display: 'block' }}>
                        <div className="blockPageOverlay"></div>
                        <div className="blockMsg">
                            <LoadingSvg />
                        </div>
                    </div>
                ) : null}
            </form>
        </div>
    );
};
